import * as XLSX from "xlsx";
import { format } from "date-fns";
import { id } from "date-fns/locale";

export type Order = {
  order_code: string;
  created_at: string;
  buyer_name: string;
  buyer_phone?: string;
  buyer_address?: string;
  products?: { name: string };
  quantity: number;
  total_price: number;
  payment_method: string;
  payment_proof_url?: string;
  status: string;
};

export function exportConfirmedOrdersToExcel(orders: Order[]) {
  const confirmedOrders = orders.filter((o) => o.status.toLowerCase() === "confirmed");
  
  if (confirmedOrders.length === 0) {
    alert("Tidak ada order dengan status 'confirmed' untuk diexport");
    return;
  }

  // Format data with proper types and localization
  const data = confirmedOrders.map((order) => ({
    "NO": confirmedOrders.indexOf(order) + 1,
    "KODE ORDER": order.order_code,
    "TANGGAL": format(new Date(order.created_at), "dd/MM/yyyy HH:mm", { locale: id }),
    "NAMA PEMBELI": order.buyer_name,
    "NO. HP": order.buyer_phone || "-",
    "ALAMAT": order.buyer_address || "-",
    "PRODUK": order.products?.name || "-",
    "JUMLAH": order.quantity,
    "HARGA SATUAN": order.total_price / order.quantity,
    "TOTAL HARGA": order.total_price,
    "METODE PEMBAYARAN": order.payment_method,
    "BUKTI PEMBAYARAN": order.payment_proof_url || "-",
    "STATUS": "CONFIRMED" // Force uppercase for consistency
  }));

  // Create workbook and worksheet
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(data);

  // Add styling through cell properties
  const headerStyle = {
    font: { bold: true, color: { rgb: "FFFFFF" } },
    fill: { fgColor: { rgb: "2C3E50" } },
    alignment: { horizontal: "center" }
  };

  // Apply header styling
  const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1:Z1");
  for (let C = range.s.c; C <= range.e.c; ++C) {
    const cell = XLSX.utils.encode_cell({ c: C, r: range.s.r });
    if (!worksheet[cell]) continue;
    worksheet[cell].s = headerStyle;
  }

  // Set column widths
  worksheet["!cols"] = [
    { wch: 5 },  // NO
    { wch: 15 }, // KODE ORDER
    { wch: 18 }, // TANGGAL
    { wch: 20 }, // NAMA PEMBELI
    { wch: 15 }, // NO. HP
    { wch: 30 }, // ALAMAT
    { wch: 25 }, // PRODUK
    { wch: 8 },  // JUMLAH
    { wch: 15 }, // HARGA SATUAN
    { wch: 15 }, // TOTAL HARGA
    { wch: 20 }, // METODE PEMBAYARAN
    { wch: 30 }, // BUKTI PEMBAYARAN
    { wch: 12 }  // STATUS
  ];

  // Format currency columns
  const currencyColumns = ["HARGA SATUAN", "TOTAL HARGA"];
  const currencyStyle = { numFmt: '"Rp"#,##0;-"Rp"#,##0' };
  
  Object.keys(worksheet).forEach((cell) => {
    if (cell[0] === "!") return;
    const colName = worksheet[cell].v;
    
    if (currencyColumns.includes(colName)) {
      const colLetter = cell.replace(/[0-9]/g, "");
      const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1:Z100");
      
      for (let R = range.s.r + 1; R <= range.e.r; ++R) {
        const cellRef = XLSX.utils.encode_cell({ c: range.s.c, r: R });
        if (worksheet[cellRef]) {
          worksheet[cellRef].s = currencyStyle;
        }
      }
    }
  });

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(
    workbook, 
    worksheet, 
    `Confirmed Orders ${format(new Date(), "yyyy-MM-dd")}`
  );

  // Generate filename with current date
  const fileName = `Confirmed_Orders_${format(new Date(), "yyyyMMdd_HHmm")}.xlsx`;

  // Export the workbook
  XLSX.writeFile(workbook, fileName, {
    bookType: "xlsx",
    type: "array",
    cellDates: true
  });
}